import React, { useState, useEffect } from 'react';
import { BarChart3, LayoutGrid, Layers, LogOut, Globe, UserCheck, Menu, Mail } from 'lucide-react';
import { logoutAdmin, getAdminEmail, getMessages, getMessagesAsync } from '../../services/adminStorage';
import MetricsPanel from './MetricsPanel';
import ModelsManager from './ModelsManager';
import CategoriesManager from './CategoriesManager';
import MessagesManager from './MessagesManager';

export default function AdminLayout({ onLogout, onBackToStore }) {
  const [activeTab, setActiveTab] = useState('metrics');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const checkUnread = () => {
      const msgs = getMessages();
      const unread = msgs.filter(m => !m.is_read).length;
      setUnreadCount(unread);
    };

    checkUnread();
    getMessagesAsync().then(checkUnread);

    window.addEventListener('suzume_data_updated', checkUnread);
    return () => window.removeEventListener('suzume_data_updated', checkUnread);
  }, []);

  const handleLogout = () => {
    logoutAdmin();
    onLogout();
  };

  const navItems = [
    { id: 'metrics', label: 'Métricas & Analytics', icon: BarChart3 },
    { id: 'messages', label: 'Caixa de Entrada', icon: Mail, badge: unreadCount },
    { id: 'models', label: 'Gerenciar Modelos', icon: LayoutGrid },
    { id: 'categories', label: 'Nichos & Categorias', icon: Layers }
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--bg-main)' }}>
      
      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(11, 19, 43, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 99
          }}
        />
      )}

      {/* Sidebar Corporativo */}
      <aside style={{
        width: '260px',
        backgroundColor: '#0b132b',
        color: '#94a3b8',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '1.75rem 1.25rem',
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 100,
        transition: 'transform 0.3s ease',
        transform: isMobileOpen ? 'translateX(0)' : undefined
      }}>
        <div>
          {/* Header Branding */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', paddingBottom: '1.25rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <img 
              src="/img/logo_suzume_horizontal.png" 
              alt="Suzume Technologies" 
              style={{ height: '36px', width: 'auto', filter: 'brightness(0) invert(1)' }}
              onError={(e) => e.target.style.display = 'none'} 
            />
            <span style={{ fontSize: '0.75rem', color: '#3a86ff', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Painel Admin
            </span>
          </div>

          {/* User Logged Info */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '0.75rem 1rem',
            marginBottom: '1.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.65rem'
          }}>
            <UserCheck size={18} color="#2563eb" />
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ffffff' }}>Admin Conectado:</div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getAdminEmail()}
              </div>
            </div>
          </div>

          {/* Nav Items */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    width: '100%',
                    padding: '0.8rem 1rem',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: isActive ? 'var(--color-brand-primary)' : 'transparent',
                    color: isActive ? '#ffffff' : '#94a3b8',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Icon size={18} color={isActive ? '#ffffff' : '#94a3b8'} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <span style={{
                      backgroundColor: '#ef4444',
                      color: '#ffffff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '12px',
                      lineHeight: 1
                    }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bottom Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.25rem' }}>
          <button
            onClick={onBackToStore}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.15)',
              backgroundColor: 'transparent',
              color: '#ffffff',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Globe size={16} color="#3a86ff" />
            <span>Ver Site Público</span>
          </button>

          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            <LogOut size={16} />
            <span>Sair do Painel</span>
          </button>
        </div>
      </aside>

      {/* Main View Area */}
      <div style={{ flexGrow: 1, marginLeft: '260px', padding: '2.5rem 3rem', maxWidth: 'calc(100vw - 260px)', minWidth: 0 }}>
        
        {/* Mobile Header Toggle */}
        <div style={{ display: 'none', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setIsMobileOpen(true)}>
            <Menu size={20} /> Menu Admin
          </button>
        </div>

        {activeTab === 'metrics' && <MetricsPanel />}
        {activeTab === 'messages' && <MessagesManager />}
        {activeTab === 'models' && <ModelsManager />}
        {activeTab === 'categories' && <CategoriesManager />}
      </div>

    </div>
  );
}
